import styled from 'styled-components/macro';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import addWeeks from 'date-fns/addWeeks';
import addDays from 'date-fns/addDays';

import { useTranslation } from 'react-i18next';
import { InsightCheckoutKpis } from '../../../types/insight';
import { Money } from '../../../components/Formatters';
import { border, colors } from '../../../styles/constants';
import { Link } from 'react-router-dom';
import { useAccountId } from '../../../auth/useAccountId';
import { MarginTopCard, CardDescription, CardTitle } from '../../common';
import { LoadingOverlay } from '../../../components/Loading';
import { NoData } from './EmptyStates';
import { useState } from 'react';

const getDataAfter = (kpis: InsightCheckoutKpis, currency: string, after: Date) => {
    const filtered = kpis.values.filter(d => 'currency' in d && d.currency === currency).filter(datum => new Date(datum.date) < endOfDay(after));
    const sum = filtered.reduce((sum, datum) => datum.value + sum, 0);
    return sum;
};

const getDataGroupAfter = (kpisCollection: InsightCheckoutKpis[], currency: string, after: Date) => {
    const countKpis = kpisCollection.find(kpi => kpi.kpi_name === 'count');
    const volumeKpis = kpisCollection.find(kpi => kpi.kpi_name === 'sum');
    return {
        after,
        count: countKpis ? getDataAfter(countKpis, currency, after) : 0,
        volume: volumeKpis ? getDataAfter(volumeKpis, currency, after) : 0,
    };
};

const getParams = (currency: string, before: Date, after: Date) => (
    `created_at.lte=${endOfDay(before).toISOString()}&created_at.gte=${startOfDay(after).toISOString()}&status=AUTHORIZED&currency=${currency}`
);

const OpenTransactions = ({
    openKpisCollection,
    openKpisCreatedAtGte,
    currencies,
    loading,
}: {
    openKpisCollection: InsightCheckoutKpis[];
    openKpisCreatedAtGte: Date;
    currencies: string[];
    loading: boolean;
}) => {
    const { t } = useTranslation();
    const now = new Date();
    const [currency, setCurrency] = useState<string>(currencies[0]);
    const oneDayOpen = getDataGroupAfter(openKpisCollection, currency, addDays(now, -1));
    const oneWeekOpen = getDataGroupAfter(openKpisCollection, currency, addWeeks(now, -1));
    const twoWeeksOpen = getDataGroupAfter(openKpisCollection, currency, addWeeks(now, -2));
    const fourWeeksOpen = getDataGroupAfter(openKpisCollection, currency, addWeeks(now, -4));
    return (
        <MarginTopCard marginBottom="18px">
            {loading && <LoadingOverlay />}
            {currencies.length > 1 && <Group position="apart">
                <div />
                <Group>
                    {currencies.map(c => <FilterButton key={c} className={c === currency ? 'active' : undefined} onClick={() => setCurrency(c)}>{c}</FilterButton>)}
                </Group>
            </Group>}
            <CardTitle>{t('dashboard.kpis.open_transactions.title')}</CardTitle>
            <CardDescription>{t('dashboard.kpis.open_transactions.description')}</CardDescription>
            <ColumnsRow>
                <DataColumn
                    label={
                        t(
                            'dashboard.kpis.open_transactions.column_title',
                            {
                                timespan: t('dashboard.kpis.open_transactions.timespan.day', { count: 1 }),
                            }
                        )}
                    count={oneDayOpen.count}
                    volume={oneDayOpen.volume}
                    currency={currency}
                    params={getParams(currency, addDays(now, -1), openKpisCreatedAtGte)}
                    loading={loading}
                />
                <VerticalLine />
                <DataColumn
                    label={
                        t(
                            'dashboard.kpis.open_transactions.column_title',
                            {
                                timespan: t('dashboard.kpis.open_transactions.timespan.week', { count: 1 }),
                            }
                        )}
                    count={oneWeekOpen.count}
                    volume={oneWeekOpen.volume}
                    currency={currency}
                    params={getParams(currency, addWeeks(now, -1), openKpisCreatedAtGte)}
                    loading={loading}
                />
                <VerticalLine />
                <DataColumn
                    label={
                        t(
                            'dashboard.kpis.open_transactions.column_title',
                            {
                                timespan: t('dashboard.kpis.open_transactions.timespan.week', { count: 2 }),
                            }
                        )}
                    count={twoWeeksOpen.count}
                    volume={twoWeeksOpen.volume}
                    currency={currency}
                    params={getParams(currency, addWeeks(now, -2), openKpisCreatedAtGte)}
                    loading={loading}
                />
                <VerticalLine />
                <DataColumn
                    label={
                        t(
                            'dashboard.kpis.open_transactions.column_title',
                            {
                                timespan: t('dashboard.kpis.open_transactions.timespan.week', { count: 4 }),
                            }
                        )}
                    count={fourWeeksOpen.count}
                    volume={fourWeeksOpen.volume}
                    currency={currency}
                    params={getParams(currency, addWeeks(now, -4), openKpisCreatedAtGte)}
                    loading={loading}
                />
            </ColumnsRow>
        </MarginTopCard>
    );
};

const DataColumn = ({ label, count, volume, currency, params, loading }: { label: string; count: number; volume: number; currency: string; params: string; loading: boolean}) => {
    const { t } = useTranslation();
    const accountId = useAccountId();
    if (!count && !loading) {
        return <NoData />;
    }
    return (
        <Column to={`/${accountId}/payments/transactions/?${params}`}>
            <HoverText className="visible-on-hover">
                {t('dashboard.kpis.open_transactions.link_hover')}
            </HoverText>
            <DataColumnSmallText>
                {label}
            </DataColumnSmallText>
            <DataColumnGroup>
                <DataColumnLargeText>
                    {loading ? '?' : count}
                </DataColumnLargeText>
                <DataColumnSmallText>
                    {t('dashboard.kpis.open_transactions.transactions', { count })}
                </DataColumnSmallText>
            </DataColumnGroup>
            <DataColumnGroup>
                <DataColumnLargeText>
                    <Money amount={loading ? undefined : volume} currency={currency} boldCurrency />
                </DataColumnLargeText>
                <DataColumnSmallText>
                    {t('dashboard.kpis.open_transactions.available_for_capture')}
                </DataColumnSmallText>
            </DataColumnGroup>
        </Column>
    );
};

const FilterButton = styled.span`
      margin: auto 4px;
      background: none;
      color: ${colors.primary};
      border: none;
      min-width: initial;
      cursor: pointer;
      &:hover {
        color: ${colors.primaryHover};
      }
      &.active {
        color: initial;
        font-weight: 700;
      }
`;

const Group = styled.div<{position?: string}>`
    display: flex;
    justify-content: ${props => props.position === 'apart' ? 'space-between' : 'initial'};
    > span:not(span:last-of-type) {
        border-right: ${border.normal} solid ${colors.borderLight};
        padding-right: 8px;
    }
`;


const DataColumnSmallText = styled.div`
    padding-top: 8px;
    font-size: 12px;
`;

const DataColumnLargeText = styled.div`
    font-size: 24px;
    font-weight: bold;
`;

const DataColumnGroup = styled.div`
    padding-top: 16px;
`;

const VerticalLine = styled.div`
    border-left: thin solid ${colors.borderLight};

    @media (max-width:1200px) {
        display: none;
    }
`;

const HoverText = styled.div`
    position: absolute;
    top: -16px;
    right: 0px;
    left: 0px;
    font-size: 12px;
    font-weight: bold;
    visibility: hidden;
`;

const Column = styled(Link)`
    color: ${colors.text};
    text-decoration: none;
    position: relative;
    display: flex;
    flex-direction: column;
    text-align: center;
    padding-left: 16px;
    padding-right: 16px;
    padding-bottom: 24px;
    padding-top: 16px;
    &:hover {
        color: ${colors.primary};
        cursor: pointer;

        &:after{
            content: '';
            position: absolute;
            top: 0px;
            right: 0px;
            left: 0px;
            bottom: 0px;
            border-radius: 4px;
            border: 2px solid ${colors.primary};
        }
        > .visible-on-hover{
            visibility: visible;
        }
    }

    @media (min-width: 768px) and (max-width:1200px) {
        width: 50%;
        &:nth-child(1){
            border-right: 1px solid ${colors.borderLight};
            border-bottom: 1px solid ${colors.borderLight};
        }
        &:nth-child(3){
            border-bottom: 1px solid ${colors.borderLight};
        }
        &:nth-child(5){
            border-right: 1px solid ${colors.borderLight};
        }
    }

    @media (max-width: 768px) {
        width: 100%;
        border-right: none;

        text-align: center;

        &:not(:first-child){
            border-top: 1px solid ${colors.borderLight};
        }
    }
`;

const ColumnsRow = styled.div`
    padding-top: 8px;
    padding-bottom: 8px;
    display:flex;
    justify-content: space-around;
    align-items: stretch;

    @media (max-width: 768px) {
        flex-direction: column;
    }

    @media (max-width:1200px) {
        flex-wrap: wrap;
    }
`;

export default OpenTransactions;
